/**
 * Resolve a customer-provided social media handle into a full URL.
 *
 * @param {string} handle  Raw account name / URL entered by admin.
 * @param {string} [network]  Explicit network chosen by admin: 'instagram' | 'facebook' | 'tiktok'.
 *                            When provided, wins over any URL-substring inference.
 *
 * Rules:
 *   - if `network` is set → build URL for that network from a bare handle,
 *                          or return the input as-is if it already contains the network host.
 *   - else infer:
 *       starts with `@`               → https://instagram.com/<handle without @>
 *       contains `facebook.com`/`fb.com` → open as-is (add https:// if missing)
 *       contains `instagram.com`      → open as-is
 *       contains `tiktok.com`         → open as-is
 *       otherwise                     → null (unknown — do NOT guess TikTok anymore)
 *
 * Returns null if the handle is empty / whitespace-only, or when no network is set
 * and the handle cannot be safely inferred.
 */
export function resolveSocialUrl(handle, network) {
  if (!handle) return null;
  const h = handle.trim();
  if (!h) return null;

  const withScheme = (s) => (s.startsWith('http://') || s.startsWith('https://') ? s : `https://${s}`);
  const lower = h.toLowerCase();
  const cleanHandle = h.replace(/^@/, '');

  // Explicit network wins
  if (network) {
    const n = String(network).toLowerCase();
    if (n === 'instagram') {
      if (lower.includes('instagram.com')) return withScheme(h);
      return `https://instagram.com/${encodeURIComponent(cleanHandle)}`;
    }
    if (n === 'facebook') {
      if (lower.includes('facebook.com') || lower.includes('fb.com')) return withScheme(h);
      return `https://facebook.com/${encodeURIComponent(cleanHandle)}`;
    }
    if (n === 'tiktok') {
      if (lower.includes('tiktok.com')) return withScheme(h);
      return `https://tiktok.com/@${encodeURIComponent(cleanHandle)}`;
    }
  }

  // Fallback inference — only when very confident
  if (h.startsWith('@')) {
    return `https://instagram.com/${encodeURIComponent(cleanHandle)}`;
  }
  if (lower.includes('facebook.com') || lower.includes('fb.com')) return withScheme(h);
  if (lower.includes('instagram.com')) return withScheme(h);
  if (lower.includes('tiktok.com')) return withScheme(h);
  // Unknown — do NOT guess (previously assumed TikTok, which caused wrong links)
  return null;
}

export const SOCIAL_NETWORKS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
];

